import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Globe2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { mediaSrc } from "../site/HeroSlides";
import { Stories } from "../../pages/WeddingSocial";
import { WorldWeddingMap } from "./WorldWeddingMap";

function Bubble({ b, onOpen, testid }) {
  return (
    <button onClick={() => onOpen(b)} className="shrink-0 w-[72px] flex flex-col items-center gap-1 group" data-testid={testid}>
      <span className={`relative w-16 h-16 rounded-full p-[2.5px] ${b.count ? "bg-gradient-to-tr from-[var(--gold)] via-[#e8c98a] to-[#d41a24]" : "bg-white/20"} transition-transform group-hover:scale-105`}>
        <span className="block w-full h-full rounded-full overflow-hidden border-2 border-black bg-[#222]">{b.cover_image && <img src={mediaSrc(b.cover_image)} alt="" className="w-full h-full object-cover" />}</span>
        {b.live && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold bg-[#d41a24] text-white px-1.5 rounded">LIVE</span>}
      </span>
      <span className="text-[10px] text-white/80 truncate w-full text-center">{b.couple_names}</span>
    </button>
  );
}

export function StoryBubbles({ dark = true }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [map, setMap] = useState(false);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => api.get("/stories/bubbles").then((r) => setD(r.data)).catch(() => setD({ stories: [], world: [] }));
  useEffect(() => { load(); api.get("/weddings/mine/world-share").then((r) => setMine(r.data)).catch(() => {}); }, []);
  async function openB(b) {
    if (!b.count) { if (b.live) window.location.href = `/site/${b.slug}/social`; return; }
    try { const { data } = await api.get(`/stories/wedding/${b.wedding_id}`); setOpen(data); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function add(e) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post("/weddings/mine/live-stories", fd); toast.success("Added to your story for 24 hours"); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  async function world(v) {
    try { await api.put("/weddings/mine/world-share", { enabled: v }); setMine({ ...mine, enabled: v }); toast.success(v ? "You're in the 'Married around the world' ring on your wedding day" : "Removed from the world ring"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  if (!d) return null;
  return (
    <div className={`${dark ? "text-white" : ""}`} data-testid="story-bubbles">
      <div className="flex gap-3 overflow-x-auto px-4 py-2 scrollbar-none">
        {mine && (
          <button onClick={() => ref.current?.click()} disabled={busy} className="shrink-0 w-[72px] flex flex-col items-center gap-1" data-testid="story-add">
            <span className="w-16 h-16 rounded-full border-2 border-dashed border-[var(--gold)] flex items-center justify-center">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-6 h-6 text-[var(--gold)]" />}</span>
            <span className="text-[10px] text-white/80">Your story</span>
          </button>
        )}
        <input ref={ref} type="file" accept="image/*,video/*" hidden onChange={add} data-testid="story-add-input" />
        {d.stories.map((b) => <Bubble key={b.wedding_id} b={b} onOpen={openB} testid={`story-bubble-${b.slug}`} />)}
        {!d.stories.length && !mine && <p className="text-xs text-white/50 py-6" data-testid="story-empty">No stories in the last 24 hours yet.</p>}
      </div>
      {(
        <div className="px-4 pb-2" data-testid="story-world">
          <div className="flex items-center justify-between gap-3 mb-1"><p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[var(--gold)]"><Globe2 className="w-3.5 h-3.5" /> Married around the world today</p>
            <button onClick={() => setMap(true)} className="text-xs bg-white/10 hover:bg-white/20 rounded-full px-3 py-1 transition-colors" data-testid="story-world-map-btn">Map</button>
            {mine && <label className="flex items-center gap-2 text-xs text-white/85 bg-white/10 rounded-full px-3 py-1" title={mine.public ? "" : "Your page is private, so only your own guests will see you in the ring"}><input type="checkbox" checked={mine.enabled} onChange={(e) => world(e.target.checked)} data-testid="story-world-toggle" /> Join on our day{!mine.public && " (guests only)"}</label>}</div>
          <div className="flex gap-3 overflow-x-auto">{d.world.map((b) => <Bubble key={b.wedding_id} b={b} onOpen={openB} testid={`story-world-${b.slug}`} />)}
            {!d.world.length && <p className="text-xs text-white/50 py-2">No weddings sharing today yet.</p>}</div>
        </div>
      )}
      {open && <Stories stories={open.items} couple={open.couple_names} start={0} onClose={() => setOpen(null)} />}
      {map && <WorldWeddingMap onClose={() => setMap(false)} onOpen={(p) => { setMap(false); openB(p); }} />}
    </div>
  );
}

export function LiveMoments({ id }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => api.get(`/live/${id}/moments`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function add(e) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const name = localStorage.getItem("lif_name") || window.prompt("Your name for this moment");
    if (!name) return;
    localStorage.setItem("lif_name", name);
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); fd.append("name", name); await api.post(`/live/${id}/moments`, fd); toast.success("Your moment is in the stream for 24 hours"); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <div className="flex gap-3 overflow-x-auto px-4 py-3 bg-black/60 border-b border-white/10" data-testid="live-moments">
      <button onClick={() => ref.current?.click()} disabled={busy} className="shrink-0 w-14 flex flex-col items-center gap-1" data-testid="live-moment-add">
        <span className="w-12 h-12 rounded-full border-2 border-dashed border-[var(--gold)] flex items-center justify-center">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5 text-[var(--gold)]" />}</span>
        <span className="text-[9px] text-white/70">Moment</span>
      </button>
      <input ref={ref} type="file" accept="image/*,video/*" capture="environment" hidden onChange={add} data-testid="live-moment-input" />
      {rows.map((m, i) => (
        <button key={m.story_id} onClick={() => setOpen(i)} className="shrink-0 w-14 flex flex-col items-center gap-1" data-testid={`live-moment-${m.story_id}`}>
          <span className="w-12 h-12 rounded-full p-[2px] bg-gradient-to-tr from-[var(--gold)] to-[#d41a24]"><span className="block w-full h-full rounded-full overflow-hidden border-2 border-black bg-[#222]">{m.type === "image" && <img src={mediaSrc(`/api/files/${m.path}`)} alt="" className="w-full h-full object-cover" />}</span></span>
          <span className="text-[9px] text-white/70 truncate w-full text-center">{m.by}</span>
        </button>
      ))}
      {!rows.length && <p className="text-xs text-white/50 self-center">Guests' moments from this stream show here.</p>}
      {open !== null && <Stories stories={rows.map((m) => ({ ...m, caption: m.caption || `From ${m.by}` }))} couple="Live moments" start={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
